// dependencies
import { cva } from "class-variance-authority";
import React from "react";
import { createPortal } from "react-dom";

import { faXmark } from "@performyard/fontawesome/pro-regular-svg-icons";

import { useKeyPress } from "../../../hooks/useKeyPress";
import { useOverlayAnimation } from "../../../hooks/useOverlayAnimation";
import { useOverlayFocus } from "../../../hooks/useOverlayFocus";
import { usePortal } from "../../../hooks/usePortal";

import { cn } from "../../../lib/utils";

import { ButtonIcon } from "../../atoms/ButtonIcon";

export interface ModalBaseProps {
	/**
	 * React node content to display inside the modal
	 */
	children: React.ReactNode;
	/**
	 * Whether the modal is open or closed
	 */
	isOpen: boolean;
	/**
	 * Callback function executed when the modal should be closed
	 */
	onClose: () => void;
	/**
	 * Optional CSS classes to apply to the modal container (use with caution)
	 */
	dangerouslySetClassName?: string;
}

const modalBaseVariants = cva(
	"relative z-10 flex max-h-[80vh] w-fit min-w-[264px] flex-col overflow-y-auto overflow-x-hidden rounded-2 bg-neutral-0 shadow-xl outline-none transition-all duration-300 ease-in-out"
);

/**
 * ModalBase component that provides a centered modal with portal rendering.
 *
 * This component serves as an unstyled foundation for building modal templates.
 * It includes essential functionality like:
 * - Portal-based rendering outside the DOM hierarchy
 * - Centered positioning that auto-sizes to content with a minimum width
 * - Built-in X close icon in the upper right corner
 * - Smooth fade and scale animations using Tailwind CSS
 * - Background scroll prevention when open
 * - Close on outside click behavior
 * - Close on Escape key press
 * - Flexible sizing that adapts to child content dimensions
 *
 * Accessibility features:
 * - Uses appropriate ARIA attributes for modal behavior
 * - Traps focus within the modal when open
 * - Supports keyboard navigation (Escape to close)
 * - Prevents background interaction when open
 */
const ModalBase = (props: ModalBaseProps) => {
	const { children, isOpen, onClose, dangerouslySetClassName } = props;

	// Animation lifecycle management
	const { shouldRender, isAnimating, animatedElementRef } = useOverlayAnimation<HTMLDivElement>({
		isOpen,
		transitionProperty: ["opacity", "transform"],
	});

	// Portal and body scroll management with context support
	const { portalRef, isRendered, PortalProvider } = usePortal({
		shouldRender,
		portalAttribute: "data-modal-base-portal",
		preventBodyScroll: true,
	});

	// Focus management for accessibility
	useOverlayFocus<HTMLDivElement>({
		isOpen,
		shouldRender,
		isRendered,
		elementRef: animatedElementRef,
	});

	useKeyPress({
		key: "Escape",
		onPress: onClose,
		disabled: !isOpen,
	});

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
			e.preventDefault();
			onClose();
		}
	};

	if (!isRendered || !portalRef.current) {
		return null;
	}

	return createPortal(
		<PortalProvider>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="modal-content"
				className="fixed inset-0 z-[110] flex items-center justify-center p-4"
			>
				<div
					role="button"
					tabIndex={-1}
					aria-label="Close modal"
					className={`fixed inset-0 bg-neutral-9/50 transition-opacity duration-300 ${
						isAnimating ? "opacity-100" : "opacity-0"
					}`}
					onClick={handleBackdropClick}
					onKeyDown={handleBackdropKeyDown}
				/>
				<div
					ref={animatedElementRef}
					tabIndex={-1}
					className={cn(
						modalBaseVariants(),
						`transform transition-all duration-300 ease-in-out ${
							isAnimating ? "scale-100 opacity-100" : "scale-95 opacity-0"
						}`,
						dangerouslySetClassName
					)}
				>
					<div className="flex justify-end p-4 pb-0">
						<ButtonIcon
							variant="ghost"
							color="neutral"
							shade={5}
							size={24}
							icon={faXmark}
							onClick={onClose}
							aria-label="Close modal"
							data-testid="modal-confirmation-close"
						/>
					</div>
					{children}
				</div>
			</div>
		</PortalProvider>,
		portalRef.current
	);
};

ModalBase.displayName = "ModalBase";

export { ModalBase };
